/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import PropTypes from 'prop-types';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useParams } from 'react-router-dom';
import { DraggableAssetCard } from '../../molecules';
import { useOrderSpaceMutation } from '../../../hooks/useOrderSpaceMutation';
import { useAuth } from '../../../../authentication';
import { useOrganizations } from '../../../../organization/providers';

const DragAndDropContainer = ({
  columns,
  setColumns,
  children,
  currentUser,
  activeId,
  setActiveId
}) => {
  const { spaceId } = useParams();
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const orderSpaceMutation = useOrderSpaceMutation(accessToken, currentOrganization?.id);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || !active.data.current || !over.data.current) {
      setActiveId(null);
      return;
    }

    const activeContainer = active.data.current.sortable.containerId;
    const overContainer = over.data.current.sortable.containerId;

    if (activeContainer && overContainer) {
      let newColumns;
      let orderChanged = false;

      if (activeContainer === overContainer) {
        // Reordering within the same column
        const items = columns[activeContainer];
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        // Only proceed if the order actually changed
        if (oldIndex !== newIndex) {
          orderChanged = true;
          const newItems = arrayMove(items, oldIndex, newIndex);
          newColumns = { ...columns, [activeContainer]: newItems };
        }
      } else {
        // Moving between columns
        orderChanged = true;
        const sourceItems = columns[activeContainer];
        const targetItems = columns[overContainer];
        const activeIndex = sourceItems.findIndex((item) => item.id === active.id);
        const item = sourceItems[activeIndex];
        const newSourceItems = [...sourceItems];
        newSourceItems.splice(activeIndex, 1);
        const targetIndex = targetItems.findIndex((_item) => _item.id === over.id);
        const newTargetItems = [...targetItems];
        newTargetItems.splice(targetIndex, 0, item);
        newColumns = {
          ...columns,
          [activeContainer]: newSourceItems,
          [overContainer]: newTargetItems
        };
      }

      if (orderChanged && newColumns) {
        setColumns(newColumns);

        // Update the order on the server while maintaining column assignments
        const allItems = [...newColumns.left, ...newColumns.right];
        const questions = allItems.map((item) => {
          // Determine which column the item is in
          const isInLeftColumn = newColumns.left.some((leftItem) => leftItem.id === item.id);
          // Calculate order based on position within its column
          const columnItems = isInLeftColumn ? newColumns.left : newColumns.right;
          const columnIndex = columnItems.findIndex((colItem) => colItem.id === item.id);
          // Use even numbers for left column, odd for right column
          const order = isInLeftColumn ? columnIndex * 2 : columnIndex * 2 + 1;

          return {
            question_id: item.id,
            order
          };
        });

        // Make the API call to update order
        orderSpaceMutation.mutate(
          { spaceId, questions },
          {
            onError: (error) => {
              // eslint-disable-next-line no-console
              console.error('Failed to update order:', error);
              // Optionally revert the columns state if the API call fails
              setColumns(columns);
            }
          }
        );
      }
    }
    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={[...columns.left, ...columns.right]
          .sort((a, b) => a.order - b.order)
          .map((item) => item.id)}
        strategy={rectSortingStrategy}
      >
        {children}
      </SortableContext>
      <DragOverlay>
        {activeId
          ? (() => {
              const allItems = [...columns.left, ...columns.right].sort(
                (a, b) => a.order - b.order
              );
              const activeItem = allItems.find((item) => item.id === activeId);
              return activeItem ? (
                <DraggableAssetCard question={activeItem} isReadOnly={!currentUser?.canAsk} />
              ) : null;
            })()
          : null}
      </DragOverlay>
    </DndContext>
  );
};

DragAndDropContainer.propTypes = {
  columns: PropTypes.shape({
    left: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
    right: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired
  }).isRequired,
  setColumns: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  currentUser: PropTypes.shape({
    canAsk: PropTypes.bool
  }).isRequired,
  activeId: PropTypes.string.isRequired,
  setActiveId: PropTypes.func.isRequired
};

export { DragAndDropContainer };
